/**
 * ページ管理モジュール
 * ページ固有の機能（目次固定、セクション検出など）を制御
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// GSAPプラグインの登録
gsap.registerPlugin(ScrollTrigger);

/**
 * ページラッパーのセクション検出
 * section要素がない場合に.no-sectionクラスを追加
 * @returns {void}
 */
export function setupPageWrapperSectionDetection() {
  const pageWrapper = document.querySelector('#page-wrapper');
  if (pageWrapper && !pageWrapper.querySelector('section')) {
    pageWrapper.classList.add('no-section');
  }
}

/**
 * 目次（contentBlList）にfixedクラスを早期に付与
 * DOM読み込み前に実行して、一瞬fixedになっていない表示を防ぐ
 * @returns {Element|null} fixedクラスが付与された要素、見つからない場合はnull
 */
export function addFixedClassEarly() {
  const pageWrapper = document.querySelector('#page-wrapper');
  if (!pageWrapper) return null;

  const lists = document.querySelectorAll('.contentBlList');
  if (lists.length === 0) return null;

  let fixedList = null;

  // Contentまたは目次を含むリストを検索
  for (let i = 0; i < lists.length; i++) {
    const list = lists[i];
    const dts = list.querySelectorAll('dt');
    let matched = false;

    for (let j = 0; j < dts.length; j++) {
      const text = (dts[j].textContent || '').trim();
      const textLower = text.toLowerCase();
      
      // 大文字小文字を区別しない検索（"content"または"目次"）
      if (textLower.indexOf('content') !== -1 || text.indexOf('目次') !== -1) {
        matched = true;
        break;
      }
    }

    if (matched) {
      fixedList = list;
      break; // 複数あっても1つだけ固定
    }
  }

  if (!fixedList) return null;

  // fixedクラスを追加（早期実行）
  fixedList.classList.add('fixed');
  pageWrapper.classList.add('have-contentBlList');

  return fixedList;
}

/**
 * 目次（contentBlList）の固定機能を設定
 * position: fixedで固定し、ScrollTriggerを使用して#page-wrapper内でのみ表示されるように制御
 * #page-wrapperの範囲外では非表示にする
 * @returns {void}
 */
export function setupContentBlListFixed() {
  const pageWrapper = document.querySelector('#page-wrapper');
  if (!pageWrapper) return;

  // 既にクラスが付与されているか確認
  const fixedList = document.querySelector('.contentBlList.fixed');
  if (!fixedList) {
    // クラスが付与されていない場合は早期付与関数を実行
    const list = addFixedClassEarly();
    if (!list) return;
  }

  const currentFixedList = fixedList || document.querySelector('.contentBlList.fixed');
  if (!currentFixedList) return;

  // ScrollTriggerを使用して#page-wrapper内でのみ表示されるように制御
  // #page-wrapperの範囲外では非表示にする
  const mm = gsap.matchMedia();
  
  mm.add('(min-width: 992px)', () => {
    // lg以上でのみ固定が有効
    // 初期状態を非表示に設定（#page-wrapperの範囲内かどうかで判定）
    const checkVisibility = () => {
      const pageWrapperRect = pageWrapper.getBoundingClientRect();
      const scrollY = window.scrollY || window.pageYOffset;
      const pageWrapperTop = pageWrapper.offsetTop;
      const pageWrapperBottom = pageWrapperTop + pageWrapper.offsetHeight;
      
      // #page-wrapperの範囲内かどうかを判定
      if (scrollY >= pageWrapperTop && scrollY < pageWrapperBottom) {
        // #page-wrapper内では表示
        gsap.set(currentFixedList, { autoAlpha: 1 });
      } else {
        // #page-wrapper外では非表示
        gsap.set(currentFixedList, { autoAlpha: 0 });
      }
    };
    
    // 初期状態を設定
    checkVisibility();
    
    // ScrollTriggerでスクロール位置を監視し、表示/非表示を切り替え
    ScrollTrigger.create({
      trigger: pageWrapper,
      start: 'top top',
      end: 'bottom bottom',
      onEnter: () => {
        // #page-wrapperに入った時点で表示
        gsap.to(currentFixedList, { autoAlpha: 1, duration: 0.3 });
      },
      onLeave: () => {
        // #page-wrapperを出た時点で非表示
        gsap.to(currentFixedList, { autoAlpha: 0, duration: 0.3 });
      },
      onEnterBack: () => {
        // #page-wrapperに戻った時点で表示
        gsap.to(currentFixedList, { autoAlpha: 1, duration: 0.3 });
      },
      onLeaveBack: () => {
        // #page-wrapperの上端を超えた時点で非表示
        gsap.to(currentFixedList, { autoAlpha: 0, duration: 0.3 });
      },
    });
    
    // リサイズ時にも更新
    window.addEventListener('resize', () => {
      ScrollTrigger.refresh();
      checkVisibility();
    });
  });
}

/**
 * ページ機能の初期化
 * すべてのページ機能を一括で初期化
 * @returns {void}
 */
export function setupPageFeatures() {
  setupPageWrapperSectionDetection();
  setupContentBlListFixed();
}

